import type {
  Source, Page, View, PageView, KQLResponse, Claim, ClaimObject, UserClaim, SamnaUser, Setting, SettingValue,
  AISummaryResponse, ChatMessage, ChatResponse, TimeRange, RangeResponse, TraceResponse, LogSearchResponse, SearchHit, AlertRule, AlertState,
  VariableValues, PageExport, ImportResponse, GuestMode, AccessCode, AccessCodeBody, AccessCodeCreated, AlertWebhook, AlertDelivery, ViewTemplate, QueryReference, Annotation,
  MetricsQueryResponse, MetricNamesResponse, MetricLabelsResponse,
  LogsQueryResponse, LogFieldsResponse, LogValuesResponse,
  TraceSearchResponse, TraceLatencyResponse, TraceOperationsResponse, TraceServicesResponse, TraceGraphResponse, TraceSearchFilter,
  MeClaim, Notification, Organization, Retention, RetentionSignal, RoutingRule, SamplingRule, Storage, ViewSignal
} from '@/types'
import { withCache } from './cache'

const SERVER_ENDPOINT = (import.meta.env?.VITE_SERVER_ENDPOINT ?? '/api').replace(/\/$/, '')

const u = (path: string) => `${SERVER_ENDPOINT}${path}`

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  body?: unknown
  headers?: Record<string, string>
  cache?: boolean
  cacheTTL?: number
  signal?: AbortSignal
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

const activeRequests = new Map<string, AbortController>()

let guest = false

export const setGuest = (on: boolean) => {
  guest = on
}

export const isGuest = () => guest

export const getLoginURL = () => u(`/auth/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`)

export function cancelAllRequests() {
  for (const controller of activeRequests.values()) {
    controller.abort()
  }
  activeRequests.clear()
}

export async function api<T = unknown>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', cache = false, cacheTTL = 10 * 60 * 1000 } = options
  if (cache) {
    const cacheKey = `api:${method}:${endpoint}`
    return withCache(cacheKey, () => apiInternal<T>(endpoint, options), cacheTTL)
  }
  return apiInternal<T>(endpoint, options)
}

async function apiInternal<T = unknown>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, signal } = options

  const controller = new AbortController()
  const requestKey = `${method}:${endpoint}`
  activeRequests.set(requestKey, controller)

  if (signal) {
    signal.addEventListener('abort', () => controller.abort(), { once: true })
  }

  const form = body instanceof FormData

  const csrf = method !== 'GET' ? document.cookie.split('; ').find(c => c.startsWith('sauth_csrf_token='))?.slice(17) : undefined

  const config: RequestInit = {
    method,
    credentials: 'include',
    signal: controller.signal,
    headers: {
      ...(form ? {} : { 'Content-Type': 'application/json' }),
      ...(csrf ? { 'SAuth-CSRF-Token': decodeURIComponent(csrf) } : {}),
      ...headers
    }
  }

  if (body !== undefined && method !== 'GET') {
    config.body = form ? body : JSON.stringify(body)
  }

  const url = endpoint.startsWith('http') ? endpoint : u(endpoint)

  let response: Response
  try {
    response = await fetch(url, config)
  } catch (err) {
    activeRequests.delete(requestKey)
    if ((err as DOMException)?.name === 'AbortError') {
      throw new ApiError(0, 'Request aborted')
    }
    throw new ApiError(0, `Network error: ${err instanceof Error ? err.message : 'unknown'}`)
  }

  if (!response.ok) {
    let serverMessage = ''
    try {
      const text = await response.text()
      try {
        const j = JSON.parse(text)
        serverMessage = j?.error?.message || text
      } catch {
        serverMessage = text
      }
    } catch {}
    activeRequests.delete(requestKey)
    throw new ApiError(response.status, serverMessage || response.statusText || `HTTP ${response.status}`)
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    activeRequests.delete(requestKey)
    return {} as T
  }

  try {
    return ((await response.json()) as { data: T }).data
  } finally {
    activeRequests.delete(requestKey)
  }
}

export const apiGet = <T = unknown>(endpoint: string, options?: Omit<ApiOptions, 'method'>) =>
  api<T>(endpoint, { method: 'GET', ...options })

export const apiPost = <T = unknown>(endpoint: string, body?: unknown, options?: Omit<ApiOptions, 'method' | 'body'>) =>
  api<T>(endpoint, { method: 'POST', body, ...options })

export const apiPut = <T = unknown>(endpoint: string, body?: unknown, options?: Omit<ApiOptions, 'method' | 'body'>) =>
  api<T>(endpoint, { method: 'PUT', body, ...options })

export const apiPatch = <T = unknown>(endpoint: string, body?: unknown, options?: Omit<ApiOptions, 'method' | 'body'>) =>
  api<T>(endpoint, { method: 'PATCH', body, ...options })

export const apiDelete = <T = unknown>(endpoint: string, options?: Omit<ApiOptions, 'method'>) =>
  api<T>(endpoint, { method: 'DELETE', ...options })

export interface SourceLogsParams {
  kql?: string
  json?: string
  from?: string
  to?: string
  take?: number
  poll_ms?: number
}

const buildLogsQuery = (params?: SourceLogsParams): string => {
  if (!params) return ''
  const sp = new URLSearchParams()
  if (params.kql) sp.set('kql', params.kql)
  if (params.json) sp.set('json', params.json)
  if (params.from) sp.set('from', params.from)
  if (params.to) sp.set('to', params.to)
  if (params.take != null) sp.set('take', String(params.take))
  if (params.poll_ms != null) sp.set('poll_ms', String(params.poll_ms))
  const s = sp.toString()
  return s ? `?${s}` : ''
}

export const getSourceLogs = <T = unknown>(sourceId: string, params?: SourceLogsParams) =>
  apiGet<T>(`/sources/${sourceId}/logs${buildLogsQuery(params)}`)

const rangeQuery = (range: TimeRange, extra: Record<string, string | undefined> = {}, vars: VariableValues = {}): string => {
  const sp = new URLSearchParams()
  sp.set('from', range.from)
  sp.set('to', range.to)
  sp.set('step', range.step)
  for (const [k, v] of Object.entries(extra)) if (v) sp.set(k, v)
  for (const [k, values] of Object.entries(vars)) for (const v of values) sp.append(`var.${k}`, v)
  return `?${sp.toString()}`
}

export const getSourceRange = (sourceId: string, range: TimeRange, vars: VariableValues = {}, extra: Record<string, string | undefined> = {}) =>
  apiGet<RangeResponse>(`/sources/${sourceId}/range${rangeQuery(range, extra, vars)}`)

export const getPageExport = (id: string) => apiGet<PageExport>(`/pages/${id}/export`)
export const postPageImport = (doc: PageExport) => apiPost<ImportResponse>('/pages/import', doc)

export const getTrace = (traceId: string) => apiGet<TraceResponse>(`/traces/${encodeURIComponent(traceId)}`)

const traceQuery = (f: TraceSearchFilter): Record<string, string | undefined> => {
  const out: Record<string, string | undefined> = {
    service: f.service || undefined,
    operation: f.operation || undefined,
    min_duration: f.min_duration != null ? String(f.min_duration) : undefined,
    max_duration: f.max_duration != null ? String(f.max_duration) : undefined,
    status: f.status || undefined,
    take: f.take != null ? String(f.take) : undefined
  }
  for (const [k, v] of Object.entries(f.attributes ?? {})) if (k && v) out[`attr.${k}`] = v
  return out
}

export const getTraceSearch = (f: TraceSearchFilter, range: TimeRange) =>
  apiGet<TraceSearchResponse>(`/traces/search${rangeQuery(range, traceQuery(f))}`)

export const getTraceLatency = (f: TraceSearchFilter, range: TimeRange, buckets?: number) =>
  apiGet<TraceLatencyResponse>(`/traces/latency${rangeQuery(range, { ...traceQuery(f), buckets: buckets != null ? String(buckets) : undefined })}`)

export const getTraceOperations = (range: TimeRange, service?: string) =>
  apiGet<TraceOperationsResponse>(`/traces/operations${rangeQuery(range, { service })}`)

export const getTraceServices = (range: TimeRange) =>
  apiGet<TraceServicesResponse>(`/traces/services${rangeQuery(range)}`)

export const getTraceGraph = (range: TimeRange) =>
  apiGet<TraceGraphResponse>(`/traces/graph${rangeQuery(range)}`)

export const getLogSearch = (q: string, range: TimeRange, service?: string, take?: number) =>
  apiGet<LogSearchResponse>(`/logs/search${rangeQuery(range, { q, service, take: take != null ? String(take) : undefined })}`)

export const getSearch = (q: string, limit: number) =>
  apiGet<SearchHit[]>(`/search?${new URLSearchParams({ q, limit: String(limit) })}`)

export const getLogsQuery = (q: string, range: TimeRange, take?: number) =>
  apiGet<LogsQueryResponse>(`/logs/query${rangeQuery(range, { q, take: take != null ? String(take) : undefined })}`)

export const getLogFields = (range: TimeRange) =>
  apiGet<LogFieldsResponse>(`/logs/fields${rangeQuery(range)}`)

export const getLogValues = (field: string, range: TimeRange) =>
  apiGet<LogValuesResponse>(`/logs/values${rangeQuery(range, { field })}`)

export const getMetricsQuery = (expr: string, range: TimeRange) =>
  apiGet<MetricsQueryResponse>(`/metrics/query${rangeQuery(range, { expr })}`)

export const getQueryReference = () => apiGet<QueryReference>('/query/reference')

export const getMetricNames = () => apiGet<MetricNamesResponse>('/metrics/names')

export const getMetricLabels = (name: string) =>
  apiGet<MetricLabelsResponse>(`/metrics/labels?name=${encodeURIComponent(name)}`)

export const getAlerts = () => apiGet<AlertRule[]>('/alerts')
export const getAlert = (id: string) => apiGet<AlertRule>(`/alerts/${id}`)
export const getAlertState = (id: string) => apiGet<AlertState>(`/alerts/${id}/state`)
export const postAlert = (r: Partial<AlertRule>) => apiPost<AlertRule>('/alerts', r)
export const patchAlert = (id: string, r: Partial<AlertRule>) => apiPatch<AlertRule>(`/alerts/${id}`, r)
export const deleteAlert = (id: string) => apiDelete<void>(`/alerts/${id}`)
export const getAlertWebhooks = (id: string) => apiGet<AlertWebhook[]>(`/alerts/${id}/webhooks`)
export const postAlertWebhook = (id: string, url: string) => apiPost<AlertWebhook>(`/alerts/${id}/webhooks`, { url })
export const patchAlertWebhook = (id: string, webhookId: string, w: Partial<AlertWebhook>) => apiPatch<AlertWebhook>(`/alerts/${id}/webhooks/${webhookId}`, w)
export const postAlertWebhookSecret = (id: string, webhookId: string) => apiPost<AlertWebhook>(`/alerts/${id}/webhooks/${webhookId}/secret`)
export const deleteAlertWebhook = (id: string, webhookId: string) => apiDelete<void>(`/alerts/${id}/webhooks/${webhookId}`)
export const getAlertDeliveries = (id: string, webhookId: string) => apiGet<AlertDelivery[]>(`/alerts/${id}/webhooks/${webhookId}/deliveries`)


export const getSources = () => apiGet<Source[]>('/sources')
export const getSource = (id: string) => apiGet<Source>(`/sources/${id}`)
export const putSource = (s: Partial<Source> & { secrets?: Partial<Record<import('@/types').SourceSecretKey, string>> }) => apiPut<Source & { secret?: string }>(s.id ? `/sources/${s.id}` : '/sources', s)
export const deleteSource = (id: string) => apiDelete<void>(`/sources/${id}`)
export const postSourceValidate = (id: string) =>
  apiPost<{ state: import('@/types').SourceState }>(`/sources/${id}/validate`)
export const postSourceCopy = (sourceId: string) =>
  apiPost<Source>('/sources/copy', { source_id: sourceId })
export const postSourceQuery = (id: string, query: string, opts?: { timespan?: string; scope?: 'source' | 'workspace' }) =>
  apiPost<KQLResponse>(`/sources/${id}/query`, { query, timespan: opts?.timespan, scope: opts?.scope })

export const getPages = () => apiGet<Page[]>('/pages')
export const getPage = (id: string) => apiGet<Page>(`/pages/${id}`)
export const postPage = (s: Partial<Page>) => apiPost<Page>('/pages', s)
export const putPage = (s: Page) => apiPut<Page>(`/pages/${s.id}`, s)
export const deletePage = (id: string) => apiDelete<void>(`/pages/${id}`)
export const postPageAccess = (id: string, message?: string) => apiPost<{ page_id: string; sent: number }>(`/pages/${id}/access`, { message })
export const deletePageAccess = (id: string, userId: string) => apiDelete<void>(`/pages/${id}/access/${userId}`)

export const getNotifications = () => apiGet<Notification[]>('/notification')
export const putNotificationRead = (id: string) => apiPut<Notification>(`/notification/${id}/read`)
export const deleteNotification = (id: string) => apiDelete<{ id: string }>(`/notification/${id}`)

export type ClaimResource = 'pages' | 'views' | 'sources' | 'alerts' | 'annotations'
export const getObjectClaims = (resource: ClaimResource, id: string) => apiGet<Claim[]>(`/${resource}/${id}/claims`)
export const postClaim = (c: Partial<Claim>) => apiPost<Claim>('/claims', c)
export const postClaimObject = (claimId: string, o: Partial<ClaimObject>) => apiPost<ClaimObject>(`/claims/${claimId}/objects`, o)
export const deleteClaimObject = (claimId: string, bindingId: string) => apiDelete<ClaimObject>(`/claims/${claimId}/objects/${bindingId}`)
export const postClaimUser = (claimId: string, userId: string) => apiPost<UserClaim>(`/claims/${claimId}/users`, { user_id: userId })
export const deleteClaimUser = (claimId: string, grantId: string) => apiDelete<UserClaim>(`/claims/${claimId}/users/${grantId}`)
export const patchClaim = (claimId: string, c: Partial<Claim>) => apiPatch<Claim>(`/claims/${claimId}`, c)
export const deleteClaim = (claimId: string) => apiDelete<Claim>(`/claims/${claimId}`)
export const putClaimGuest = (claimId: string, mode: GuestMode) => apiPut<Claim>(`/claims/${claimId}/guest`, { mode })
export const getClaimCodes = (claimId: string) => apiGet<AccessCode[]>(`/claims/${claimId}/codes`)
export const postClaimCode = (claimId: string, body: AccessCodeBody) => apiPost<AccessCodeCreated>(`/claims/${claimId}/codes`, body)
export const deleteClaimCode = (claimId: string, codeId: string) => apiDelete<AccessCode>(`/claims/${claimId}/codes/${codeId}`)
export const postClaimEmail = (claimId: string, body: { user_id: string; path: string; message?: string }) => apiPost<{ user_id: string; url: string }>(`/claims/${claimId}/emails`, body)

export const getUsers = () => apiGet<SamnaUser[]>('/users')

export const getMeSettings = () => apiGet<Setting>('/me/settings')
export const putMeSettings = (value: SettingValue) => apiPut<Setting>('/me/settings', value)
export const getOrganizationSettings = (id: string) => apiGet<Setting>(`/organizations/${id}/settings`)
export const putOrganizationSettings = (id: string, value: SettingValue) => apiPut<Setting>(`/organizations/${id}/settings`, value)
export type SettingResource = 'pages' | 'views'
export const getObjectSettings = (resource: SettingResource, id: string) => apiGet<Setting>(`/${resource}/${id}/settings`)
export const putObjectSettings = (resource: SettingResource, id: string, value: SettingValue) => apiPut<Setting>(`/${resource}/${id}/settings`, value)

export const getClaim = (id: string) => apiGet<Claim>(`/claims/${id}?with=claim_object&format=string`)
export const getMeClaims = () => apiGet<MeClaim[]>('/me/claim')
export const getGuest = async (): Promise<boolean> => {
  const res = await fetch(u('/me/claim'), { credentials: 'include' })
  if (res.status === 401 || res.status === 403) return true
  if (!res.ok) return false
  const claims = ((await res.json()) as { data: MeClaim[] }).data
  return claims.length > 0 && claims.every((c) => c.guest_mode === 'open' || c.guest_mode === 'code')
}
export const getMeOrganizations = () => apiGet<Organization[]>('/me/organization')

export const getMeProfileUrl = () => u('/me/profile')

export const getRetentions = () => apiGet<Retention[]>('/retention')
export const putRetention = (r: { signal: RetentionSignal; class: string; days: number }) => apiPut<Retention>('/retention', r)
export const getStorage = () => apiGet<Storage>('/storage')
export const putStorage = (s: Storage) => apiPut<Storage>('/storage', s)
export const getRoutingRules = () => apiGet<RoutingRule[]>('/routing')
export const postRoutingRule = (r: Partial<RoutingRule>) => apiPost<RoutingRule>('/routing', r)
export const patchRoutingRule = (id: string, r: Partial<RoutingRule>) => apiPatch<RoutingRule>(`/routing/${id}`, r)
export const deleteRoutingRule = (id: string) => apiDelete<void>(`/routing/${id}`)
export const getSamplingRules = () => apiGet<SamplingRule[]>('/sampling')
export const postSamplingRule = (r: Partial<SamplingRule>) => apiPost<SamplingRule>('/sampling', r)
export const patchSamplingRule = (id: string, r: Partial<SamplingRule>) => apiPatch<SamplingRule>(`/sampling/${id}`, r)
export const deleteSamplingRule = (id: string) => apiDelete<void>(`/sampling/${id}`)

export const getPageViews = (id: string) => apiGet<PageView[]>(`/pages/${id}/views`)
export const postPageView = (id: string, v: Partial<PageView>) =>
  apiPost<PageView>(`/pages/${id}/views`, v)
export const putPageView = (sv: PageView) => apiPut<PageView>(`/page_view/${sv.id}`, sv)
export const deletePageView = (id: string) => apiDelete<void>(`/page_view/${id}`)
export const getPageView = (id: string) => apiGet<PageView>(`/page_view/${id}`)

export const getViews = () => apiGet<View[]>('/views')
export const getView = (id: string) => apiGet<View>(`/views/${id}`)
export const postView = (v: Partial<View>) => apiPost<View>('/views', v)
export const putView = (v: View) => apiPut<View>(`/views/${v.id}`, v)
export const deleteView = (id: string) => apiDelete<void>(`/views/${id}`)
export const getViewSnapshotUrl = (v: View) => v.snapshot_at ? u(`/views/${v.id}/snapshot?at=${encodeURIComponent(v.snapshot_at)}`) : null
export const putViewSnapshot = (id: string, image: Blob) => {
  const form = new FormData()
  form.append('snapshot', image)
  return apiPut<View>(`/views/${id}/snapshot`, form)
}
export const postViewCopy = (copy: { source_view_id?: string; template_id?: string; owner_id?: string }) =>
  apiPost<View>('/views/copy', copy)
export const getViewTemplates = () => apiGet<ViewTemplate[]>('/views/templates')

export interface ViewDraft {
  description?: string | null
  signal?: ViewSignal
  name: string
  data?: string
  component?: string
  style?: string
}

export interface ActionPage {
  id: string
  name: string
  description?: string
  shared: boolean
  claims: string[]
}

export const getActionPages = () => apiGet<ActionPage[]>('/views/pages')

export interface ViewDraftResponse {
  session_id: string
  reply: string
  draft?: ViewDraft
  tool_results?: Array<Record<string, unknown>>
}

export interface ViewDiagnostic {
  source: 'data' | 'component' | 'style'
  message: string
  line: number
  column: number
  severity?: 'warning'
}

export interface ViewCheck {
  diagnostics: ViewDiagnostic[]
  module?: string
}

export interface ViewCheckRequest {
  signal?: ViewSignal
  data?: string
  component?: string
  style?: string
  module?: string
}

export const postViewCheck = (check: ViewCheckRequest) => apiPost<ViewCheck>('/views/check', check)

export interface ViewData {
  from: string
  to: string
  rows: Record<string, unknown>[]
  annotations: Annotation[]
  error?: string
}

export interface PageData {
  from: string
  to: string
  views: Record<string, ViewData>
}

export interface DataQuery {
  from?: string
  to?: string
  step?: string
  variables?: VariableValues
  draft?: string
}

export interface ViewRunRequest extends DataQuery {
  data: string
  signal: ViewSignal
  sample?: boolean
}

export interface DataFrame {
  type: 'data' | 'error'
  id?: string
  data?: ViewData
  error?: string
}

const dataQuery = (q: DataQuery): string => {
  const sp = new URLSearchParams()
  if (q.from) sp.set('from', q.from)
  if (q.to) sp.set('to', q.to)
  if (q.step) sp.set('step', q.step)
  for (const [k, values] of Object.entries(q.variables ?? {})) for (const v of values) sp.append(`var.${k}`, v)
  if (q.draft) sp.set('draft', q.draft)
  const s = sp.toString()
  return s ? `?${s}` : ''
}

export const getViewData = (id: string, q: DataQuery = {}) => apiGet<ViewData>(`/views/${id}/data${dataQuery(q)}`)

export const getPageData = (id: string, q: DataQuery = {}) => apiGet<PageData>(`/pages/${id}/data${dataQuery(q)}`)

export const postViewRun = (run: ViewRunRequest) => apiPost<ViewData>('/views/run', run)

export interface ViewRunDraft {
  id: string
  expires_at: string
}

export const postViewRunDraft = (run: ViewRunRequest) => apiPost<ViewRunDraft>('/views/run/draft', run)

export interface DataStream {
  close: () => void
}

interface StreamResult {
  status: number
  body?: { error?: { message?: string } }
}

const STREAM_FRAME = 'frame'
const STREAM_RESULT = 'result'
const STREAM_NOTIFICATION = 'notification'

let streamNotification: ((raw: string) => void) | null = null
let streamLive: ((live: number) => void) | null = null
let liveStreams = 0

export const setStreamNotifications = (onNotification: (raw: string) => void, onLive: (live: number) => void) => {
  streamNotification = onNotification
  streamLive = onLive
}

export const openDataStream = (path: string, q: DataQuery, onFrame: (frame: DataFrame) => void): DataStream => {
  const base = SERVER_ENDPOINT.startsWith('http') ? SERVER_ENDPOINT : `${window.location.origin}${SERVER_ENDPOINT}`
  const query = dataQuery(q)
  const carry = streamNotification != null
  const source = new EventSource(`${base}${path}${query}${carry ? `${query ? '&' : '?'}notifications=1` : ''}`, { withCredentials: true })
  let live = false
  const putLive = (next: boolean) => {
    if (!carry || live === next) return
    live = next
    liveStreams += next ? 1 : -1
    streamLive?.(liveStreams)
  }
  source.addEventListener('open', () => putLive(true))
  source.addEventListener('error', () => putLive(false))
  source.addEventListener(STREAM_NOTIFICATION, (e) => streamNotification?.((e as MessageEvent<string>).data))
  source.addEventListener(STREAM_FRAME, (e) => {
    try {
      onFrame(JSON.parse((e as MessageEvent<string>).data) as DataFrame)
    } catch {
      onFrame({ type: 'error', error: 'The server sent a frame that is not JSON' })
    }
  })
  source.addEventListener(STREAM_RESULT, (e) => {
    source.close()
    putLive(false)
    try {
      const result = JSON.parse((e as MessageEvent<string>).data) as StreamResult
      onFrame({ type: 'error', error: result.body?.error?.message ?? `The stream answered ${result.status}` })
    } catch {
      onFrame({ type: 'error', error: 'The stream was refused' })
    }
  })
  return {
    close: () => {
      source.close()
      putLive(false)
    },
  }
}

export const getAnnotations = (pageViewId?: string) =>
  apiGet<Annotation[]>(`/annotations${pageViewId ? `?page_view_id=${encodeURIComponent(pageViewId)}` : ''}`)
export const postAnnotation = (a: Partial<Annotation>) => apiPost<Annotation>('/annotations', a)
export const patchAnnotation = (id: string, a: Partial<Annotation>) => apiPatch<Annotation>(`/annotations/${id}`, a)
export const deleteAnnotation = (id: string) => apiDelete<void>(`/annotations/${id}`)


export const postViewDraft = (sessionId: string, signal: ViewSignal, messages: ChatMessage[], draft?: ViewDraft, viewId?: string) =>
  apiPost<ViewDraftResponse>('/views/draft', { session_id: sessionId, signal, view_id: viewId, messages, draft })

export interface PageDraftLayout {
  mode: import('@/types').PageGridMode
  columns: number
  gap?: import('@/types').PageGap
}

export interface PagePlacementDraft {
  view_id?: string
  template_id?: string
  view?: ViewDraft
  name: string
  signal: ViewSignal
  layout: { x: number; y: number; w: number; h: number }
}

export interface PageDraft {
  name: string
  description?: string | null
  layout: PageDraftLayout
  placements: PagePlacementDraft[]
}

export interface PageDraftResponse {
  session_id: string
  reply: string
  draft?: PageDraft
  tool_results?: Array<Record<string, unknown>>
}

export const postPageDraft = (sessionId: string, messages: ChatMessage[], draft?: PageDraft) =>
  apiPost<PageDraftResponse>('/pages/draft', { session_id: sessionId, messages, draft })

export interface SourceDraft {
  id?: string
  name?: string
  description?: string | null
  kind?: import('@/types').SourceKind
  color?: string
  connection?: Partial<import('@/types').SourceConnection>
  config?: Partial<import('@/types').SourceConfig>
  auth?: import('@/types').SourceAuth | null
  mapping?: import('@/types').SourceMapping
  secret_keys?: import('@/types').SourceSecretKey[]
  secrets?: Partial<Record<import('@/types').SourceSecretKey, string>>
  api_spec?: unknown
  columns?: import('@/types').LogColumn[]
}

export interface SourceDraftResponse {
  session_id: string
  reply: string
  draft?: SourceDraft
  tool_results?: Array<Record<string, unknown>>
}

export const postSourceDraft = (sessionId: string, messages: ChatMessage[], spec?: unknown, draft?: SourceDraft) =>
  apiPost<SourceDraftResponse>('/sources/draft', { session_id: sessionId, messages, api_spec: spec, draft })

export interface SourceMappedRows {
  signal: import('@/types').MappingSignal
  logs?: Record<string, unknown>[]
  metrics?: Record<string, unknown>[]
  spans?: Record<string, unknown>[]
}

export interface SourceTest {
  kind: import('@/types').SourceKind
  ingest?: import('@/types').SourceIngest
  received: boolean
  record?: { signal: string; time: string; row: Record<string, unknown> }
  items?: unknown[]
  schema?: Record<string, unknown>
  fields?: { path: string; type: string }[]
  rows?: SourceMappedRows
  records?: { time: string; source_id: string; endpoint: string; item: Record<string, unknown> }[]
  lines?: Record<string, unknown>[]
  error?: string
}

export const postSourceSpec = (body: { url?: string; document?: string }) => apiPost<import('@/types').SourceSpec>('/sources/spec', body)

export interface RecordEndpoint {
  source_id: string
  source: string
  delivery: import('@/types').OpenAPIDelivery
  name: string
  method: string
  path: string
  summary?: string
  time?: string
  fields: import('@/types').SchemaField[]
}

export const getRecordEndpoints = () => apiGet<RecordEndpoint[]>('/views/records')

export const postSourceTest = (source: SourceDraft, since?: string) => apiPost<SourceTest>('/sources/test', { source, since })

export interface AILogEntry {
  id: string
  session_id: string
  object_id: string
  object_type: 'source' | 'view'
  role: 'user' | 'assistant' | 'tool_use' | 'tool_result' | 'system'
  content?: string
  meta?: unknown
  created_at: string
  updated_at: string
}

export interface AILogSession {
  session_id: string
  object_id: string
  object_type: 'source' | 'view'
  entries: number
  started_at: string
  ended_at: string
}

export interface AILogStats {
  average_thinking_seconds: number
  sample_count: number
}

export const getAILogSessions = () => apiGet<AILogSession[]>('/ai_log')
export const getAILogStats = () => apiGet<AILogStats>('/ai_log/stats')
export const getAILogs = (sessionId: string) => apiGet<AILogEntry[]>(`/ai_log/${sessionId}`)
export const deleteAILogs = (sessionId: string) => apiDelete<void>(`/ai_log/${sessionId}`)

export const postChat = (pageId: string, messages: ChatMessage[]) =>
  apiPost<ChatResponse>('/ai/chat', { page_id: pageId, messages })

export const postAISummarize = (sourceId: string, logs: unknown[]) =>
  apiPost<AISummaryResponse>('/ai/summarize', { source_id: sourceId, logs })
